/**
 * Browser Mode Types
 *
 * Types for the browser-based demo version of Jam SQL Studio.
 */

/**
 * Type discriminator for browser mode databases.
 */
export type BrowserModeDatabaseType = 'demo' | 'uploaded';

/**
 * Descriptor for a browser mode SQLite database.
 * These are prebuilt databases shipped with browser mode.
 */
export type BrowserModeDatabase = {
  /** Unique identifier for the database */
  id: string;
  /** Display name (e.g., "Chinook Music Store") */
  name: string;
  /** Short description explaining what the database contains */
  description: string;
  /** Path to the SQLite file relative to demo-databases/ */
  assetPath: string;
  /** Number of tables in the database (for preview) */
  tableCount: number;
  /** Size hint for the database (e.g., "< 1 MB") */
  sizeHint: string;
  /** Type discriminator (defaults to 'demo' for prebuilt DBs) */
  type?: BrowserModeDatabaseType;
};

/**
 * Descriptor for an uploaded SQLite database.
 */
export type UploadedDatabase = {
  /** Unique identifier for the database (generated from filename + timestamp) */
  id: string;
  /** Original filename */
  name: string;
  /** Always 'User-uploaded SQLite database' */
  description: string;
  /** Not applicable for uploads - empty string */
  assetPath: '';
  /** Number of tables (discovered after loading) */
  tableCount: number;
  /** File size in human-readable format */
  sizeHint: string;
  /** Type discriminator */
  type: 'uploaded';
  /** Original file size in bytes */
  fileSizeBytes: number;
  /** Original filename for export */
  originalFilename: string;
};

/**
 * State of a browser mode database connection.
 */
export type BrowserModeConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Active browser mode connection state.
 */
export type BrowserModeConnectionState = {
  /** The database descriptor (demo or uploaded) */
  database: BrowserModeDatabase | UploadedDatabase;
  /** Current connection status */
  status: BrowserModeConnectionStatus;
  /** Error message if status is 'error' */
  error?: string;
};

/**
 * Type guard to check if a database is an uploaded database.
 */
export function isUploadedDatabase(db: BrowserModeDatabase | UploadedDatabase): db is UploadedDatabase {
  return db.type === 'uploaded';
}

/**
 * Prebuilt demo databases available in browser mode.
 * These are shipped as static assets.
 */
export const BROWSER_MODE_DATABASES: BrowserModeDatabase[] = [
  {
    id: 'chinook',
    name: 'Chinook Music Store',
    description: 'Sample music store with albums, artists, tracks, and invoices',
    assetPath: 'chinook.sqlite',
    tableCount: 11,
    sizeHint: '< 1 MB',
  },
  {
    id: 'baseball',
    name: 'Lahman Baseball Stats',
    description: 'Historical baseball statistics from 1871-2019 with players, teams, batting, and pitching',
    assetPath: 'baseball.sqlite',
    tableCount: 29,
    sizeHint: '~63 MB',
  },
  {
    id: 'sample',
    name: 'Sample Database',
    description: 'Small database with customers, orders, and products',
    assetPath: 'sample.sqlite',
    tableCount: 5,
    sizeHint: '< 100 KB',
  },
];
